import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import moment from 'moment';
import { debounceTime, tap } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';
import {
    DatePrecision,
    DISPLAY_DATE_FORMATS,
    DISPLAY_TIME_FORMATS,
    MaskSettings,
    popperDateTimeOptions,
    READONLY_DATE_FORMATS,
    TimePrecision,
    TooltipSettings,
} from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { createPopper, Instance } from '@popperjs/core';
import { API_DATETIME_FORMAT, COMMON_DATE_MASK_SETTINGS } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-datetime',
    templateUrl: './input-datetime.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputDatetimeComponent
    extends BazisControlValueAccessor
    implements OnChanges, AfterViewInit, OnDestroy
{
    @ViewChild('timeInput') timeInput: ElementRef;

    @ViewChild('timePopper') timePopper: ElementRef;

    @ViewChild('dateInput') dateInput: ElementRef;

    @ViewChild('calendarPopper') calendarPopper: ElementRef;

    @Input() dateTitleKey: string = null;

    @Input() timeTitleKey: string = null;

    @Input() dateTooltipKey: string = null;

    @Input() timeTooltipKey: string = null;

    // настройки ширины полей, во всю ширину формы, или две трети
    @Input() widthForm: 'full' | 'two-thirds' = 'two-thirds';

    @Input() tooltipDateSettings: TooltipSettings = null;

    @Input() tooltipTimeSettings: TooltipSettings = null;

    @Input() placeIcon: 'left' | 'right' = 'right';

    @Input() minDate: string = null;

    @Input() maxDate: string = null;

    @Input() inputFormat: string = API_DATETIME_FORMAT;

    @Input() dateMode: 'days' | 'month' | 'year' = 'days';

    @Input() datePrecision: DatePrecision = 'days';

    @Input() timePrecision: TimePrecision = 'minutes';

    @Input() dateNoteKey: string = null;

    @Input() timeNoteKey: string = null;

    // маркеры для календаря
    @Input() marks$: Observable<{ [index: number]: string }>;

    disabled = false;

    dateValue = new FormControl();

    timeValue = new FormControl();

    calendarDateValue: Date[] = [null];

    calendarTimeValue: Date;

    displayDateFormat: string;

    displayTimeFormat: string;

    readonlyFormat: string;

    showDateCalendar = false;

    showTimeCalendar = false;

    maskDateSettings: MaskSettings;

    maskTimeSettings: MaskSettings;

    instanceTimePopper: Instance;

    instanceDatePopper: Instance;

    datapickerViewConfig;

    extendOnInit() {
        this.datapickerViewConfig = this.configurationService.datapickerViewConfig;
        this.displayDateFormat = DISPLAY_DATE_FORMATS[this.datePrecision];
        this.displayTimeFormat = DISPLAY_TIME_FORMATS[this.timePrecision];

        this.maskDateSettings = {
            ...COMMON_DATE_MASK_SETTINGS,
            mask: this.displayDateFormat.replace(/[A-Za-z]/g, '0'),
        };
        this.maskTimeSettings = {
            ...COMMON_DATE_MASK_SETTINGS,
            mask: this.displayTimeFormat.replace(/[A-Za-z]/g, '0'),
        };

        this.readonlyFormat = READONLY_DATE_FORMATS[this.timePrecision];

        this.initValidators();
        this.dateValue.valueChanges
            .pipe(
                tap(() => {
                    this.timeValue.updateValueAndValidity();
                }),
            )
            .subscribe();

        merge(this.dateValue.valueChanges, this.timeValue.valueChanges)
            .pipe(
                debounceTime(0),
                tap(() => {
                    this.setCalendarValues();
                    this.setControlValue();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        merge(this.dateValue.statusChanges, this.timeValue.statusChanges)
            .pipe(
                debounceTime(0),
                tap(() => {
                    if (this.dateValue.status === 'VALID' && this.timeValue.status === 'VALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.dateValue.errors, ...this.timeValue.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        // console.log('[DEBUG] changes', changes, this.controlDate['$config']['name']);
        if (changes.minDate || changes.maxDate) {
            this.initValidators();
            const prevDateStatus = this.dateValue.status;
            const prevTimeStatus = this.timeValue.status;
            this.dateValue.updateValueAndValidity({ emitEvent: false });
            this.timeValue.updateValueAndValidity({ emitEvent: false });
            const newDateStatus = this.dateValue.status;
            const newTimeStatus = this.timeValue.status;
            if (prevDateStatus !== newDateStatus) {
                this.dateValue.updateValueAndValidity();
            }
            if (prevTimeStatus !== newTimeStatus) {
                this.timeValue.updateValueAndValidity();
            }
        }
    }

    ngAfterViewInit() {
        const referenceTime = this.timeInput.nativeElement;
        const timePopper = this.timePopper.nativeElement;
        const referenceDate = this.dateInput.nativeElement;
        const calendarPopper = this.calendarPopper.nativeElement;

        this.instanceTimePopper = createPopper(referenceTime, timePopper, {
            ...popperDateTimeOptions,
        });

        this.instanceDatePopper = createPopper(referenceDate, calendarPopper, {
            ...popperDateTimeOptions,
        });
    }

    ngOnDestroy() {
        this.instanceTimePopper.destroy();
        this.instanceDatePopper.destroy();
    }

    initValidators() {
        const dateValidators = [];
        const timeValidators = [];
        if (this.minDate) {
            dateValidators.push(
                DateValidators.minDateValidatorFn(
                    this.displayDateFormat,
                    this.datePrecision,
                    this.minDate,
                ),
            );
            timeValidators.push(
                DateValidators.minTimeValidatorFn(
                    this.dateValue,
                    this.displayDateFormat,
                    this.displayTimeFormat,
                    this.datePrecision,
                    this.timePrecision,
                    this.minDate,
                ),
            );
        }
        if (this.maxDate) {
            dateValidators.push(
                DateValidators.maxDateValidatorFn(
                    this.displayDateFormat,
                    this.datePrecision,
                    this.maxDate,
                ),
            );
        }
        dateValidators.push(DateValidators.invalidDateValidatorFn(this.displayDateFormat));
        this.dateValue.setValidators(dateValidators);
        timeValidators.push(DateValidators.invalidTimeValidatorFn());
        this.timeValue.setValidators(timeValidators);
    }

    getMomentDateTimeValues() {
        const momentDate = moment(this.dateValue.value, this.displayDateFormat, true);
        const momentTime = moment(this.timeValue.value, this.displayTimeFormat, true);
        let controlDate = momentDate && momentDate.isValid() ? momentDate : null;
        let controlTime =
            momentTime && momentTime.isValid()
                ? moment()
                      .hours(momentTime.hours())
                      .minutes(momentTime.minutes())
                      .seconds(momentTime.seconds())
                      .milliseconds(momentTime.milliseconds())
                : null;
        let controlDateTime =
            controlDate && controlTime
                ? controlDate
                      .hours(momentTime.hours())
                      .minutes(momentTime.minutes())
                      .seconds(momentTime.seconds())
                      .milliseconds(momentTime.milliseconds())
                : null;
        return {
            date: controlDate,
            time: controlTime,
            datetime: controlDateTime,
        };
    }

    setCalendarValues() {
        const momentValues = this.getMomentDateTimeValues();
        this.calendarDateValue =
            momentValues.date && momentValues.date.isValid()
                ? [momentValues.date.toDate()]
                : [null];
        this.calendarTimeValue = momentValues.time ? momentValues.time.toDate() : null;
    }

    setControlValue() {
        const momentValues = this.getMomentDateTimeValues();
        if (momentValues.datetime) {
            this.onChange(
                this._isUtc()
                    ? momentValues.datetime.utc().format(this.inputFormat)
                    : momentValues.datetime.format(this.inputFormat),
            );
            return;
        }

        this.onChange(null);
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.dateValue.disable();
            this.timeValue.disable();
        } else {
            this.dateValue.enable();
            this.timeValue.enable();
        }
    }

    public writeValue(value): void {
        if (!this.ngControl.control) return;
        if (!value) {
            this.dateValue.setValue('');
            this.timeValue.setValue('');
            this.setCalendarValues();
            return;
        }
        const momentControlValue = this._isUtc()
            ? moment.utc(value, this.inputFormat).local()
            : moment(value, this.inputFormat);
        if (!momentControlValue.isValid()) return;
        const displayDateValue = momentControlValue.format(this.displayDateFormat);
        const displayTimeValue = momentControlValue.format(this.displayTimeFormat);
        this.dateValue.setValue(displayDateValue);
        this.timeValue.setValue(displayTimeValue);
        this.setCalendarValues();
    }

    private _isUtc() {
        return this.inputFormat.indexOf('+00:00') > -1 || this.inputFormat.indexOf('[Z]') > -1;
    }

    setValueFromDateCalendar(value: string[]) {
        this.dateValue.setValue(value[0]);
        this.showDateCalendar = false;
    }

    setValueFromTimeCalendar(value: string) {
        this.timeValue.setValue(value);
        //this.showTimeCalendar = false;
    }

    openDateCalendar(e) {
        this.showDateCalendar = !this.showDateCalendar;
        if (this.instanceDatePopper) this.instanceDatePopper.update();
    }

    openTimeCalendar(e) {
        this.showTimeCalendar = !this.showTimeCalendar;
        if (this.instanceTimePopper) this.instanceTimePopper.update();
    }

    onClearDate() {
        this.dateValue.setValue('');
    }
}
